/* Reselling Suppliers — theme behaviours.
   No framework. Every number, label and toggle this file reacts to comes from
   section settings rendered into data-* attributes, so nothing is hardcoded. */
(function () {
  'use strict';

  var T = window.theme || {};
  var routes = T.routes || {};
  var CART_SECTION = 'cart-drawer';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts); }
  function pad(n) { return String(n).padStart(2, '0'); }

  /* Locking body scroll removes the page's scrollbar, and on a desktop browser
     with classic (non-overlay) scrollbars that makes the whole page jump
     sideways by its width the moment an overlay opens. Reserve the same space
     as padding so nothing moves. */
  function lockScroll() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */
  var Cart = {
    listeners: [],

    subscribe: function (fn) { this.listeners.push(fn); },
    emit: function (cart) { this.listeners.forEach(function (fn) { fn(cart); }); },

    open: function () {
      var drawer = $('[data-cart-drawer]');
      if (!drawer) { window.location.href = routes.cart; return; }
      /* Only move focus when the drawer is actually opening. Add-to-cart calls
         open() again on an already-open drawer, and yanking focus back to the
         close button each time interrupts whatever the shopper was doing. */
      var wasClosed = !drawer.classList.contains('is-open');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.dispatchEvent(new CustomEvent('theme:overlay:open'));
      if (wasClosed) {
        var close = $('[data-cart-close]', drawer);
        if (close) close.focus();
      }
    },

    close: function () {
      var drawer = $('[data-cart-drawer]');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.dispatchEvent(new CustomEvent('theme:overlay:close'));
    },

    isOpen: function () {
      var drawer = $('[data-cart-drawer]');
      return !!(drawer && drawer.classList.contains('is-open'));
    },

    request: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: CART_SECTION, sections_url: window.location.pathname }, body))
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || T.strings.cartError);
          return data;
        });
      });
    },

    render: function (data) {
      var html = data && data.sections && data.sections[CART_SECTION];
      if (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer]');
        var current = $('[data-cart-drawer]');
        if (fresh && current) {
          var wasOpen = current.classList.contains('is-open');

          /* Re-rendering replaces the line list, which would otherwise throw
             the shopper back to the top of a scrolled drawer and drop keyboard
             focus on the button they just pressed. Note both and restore them
             once the new markup is in. */
          var oldLines = $('.cart-lines', current);
          var savedScroll = oldLines ? oldLines.scrollTop : 0;
          var active = document.activeElement;
          var focusKey = active && current.contains(active)
            ? { line: active.getAttribute('data-line-key'), label: active.getAttribute('aria-label') }
            : null;

          current.innerHTML = fresh.innerHTML;
          /* innerHTML replaces the children but not the wrapper's own
             attributes, so carry over every data-* the freshly rendered
             section computed — bindCartDrawer reads them. */
          Object.keys(fresh.dataset).forEach(function (key) {
            current.dataset[key] = fresh.dataset[key];
          });
          if (wasOpen) current.classList.add('is-open');

          var newLines = $('.cart-lines', current);
          if (newLines && savedScroll) newLines.scrollTop = savedScroll;

          if (focusKey && focusKey.line) {
            var again = current.querySelector(
              '[data-line-key="' + focusKey.line + '"][aria-label="' + focusKey.label + '"]');
            if (again) again.focus();
          }

          bindCartDrawer();
        }
      }
      this.refreshCount(data);
      this.emit(data);
    },

    /* A busy state that only appears if the request is genuinely slow. The
       updated line list is itself the feedback, so a normal-speed cart update
       should show no dimming at all — flipping the class on immediately made
       every quantity click blink. */
    BUSY_AFTER_MS: 400,

    setBusy: function (drawer, busy) {
      if (!drawer) return;
      clearTimeout(drawer._busyTimer);
      drawer._busy = !!busy;
      if (busy) {
        drawer._busyTimer = setTimeout(function () {
          drawer.classList.add('is-loading');
        }, Cart.BUSY_AFTER_MS);
      } else {
        drawer.classList.remove('is-loading');
      }
    },

    isBusy: function (drawer) { return !!(drawer && drawer._busy); },

    refreshCount: function (data) {
      var count = data && typeof data.item_count === 'number'
        ? data.item_count
        : (data && data.items ? data.items.length : null);
      if (count === null) return;
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
      var live = $('#CartLiveRegion');
      if (live) {
        live.textContent = count === 1
          ? T.strings.itemSingular.replace('{{ count }}', count)
          : T.strings.itemPlural.replace('{{ count }}', count);
      }
    },

    add: function (variantId, quantity) {
      return this.request(routes.cartAdd, {
        items: [{ id: Number(variantId), quantity: quantity || 1 }]
      }).then(function (data) {
        Cart.render(data);
        Cart.open();
        return data;
      });
    },

    change: function (key, quantity) {
      return this.request(routes.cartChange, { id: key, quantity: quantity })
        .then(function (data) { Cart.render(data); return data; });
    },

    /* Upgrade snapshot — the cart as it was just before the upsell swap, so the
       shopper can put it back. sessionStorage can throw outright in private
       mode, so every access is guarded. */
    SNAPSHOT_KEY: 'cart-upsell-snapshot',

    readSnapshot: function () {
      var raw;
      try { raw = sessionStorage.getItem(Cart.SNAPSHOT_KEY); } catch (e) { return null; }
      if (!raw) return null;
      try {
        var items = JSON.parse(raw);
        return items && items.length ? items : null;
      } catch (e) { return null; }
    },

    hasSnapshot: function () { return !!Cart.readSnapshot(); },

    clearSnapshot: function () {
      try { sessionStorage.removeItem(Cart.SNAPSHOT_KEY); } catch (e) { /* noop */ }
    },

    /* Replace the whole cart with the upsell product in one swap. */
    swapTo: function (variantId) {
      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var snapshot = cart.items.map(function (i) { return { id: i.variant_id, quantity: i.quantity }; });
          if (snapshot.length) {
            try { sessionStorage.setItem(Cart.SNAPSHOT_KEY, JSON.stringify(snapshot)); } catch (e) { /* private mode */ }
          }
          var updates = {};
          cart.items.forEach(function (i) { updates[i.key] = 0; });
          return Cart.request(routes.cartUpdate, { updates: updates });
        })
        .then(function () { return Cart.add(variantId, 1); });
    },

    undoSwap: function () {
      var items = Cart.readSnapshot();
      if (!items) return Promise.resolve();
      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var updates = {};
          cart.items.forEach(function (i) { updates[i.key] = 0; });
          return Cart.request(routes.cartUpdate, { updates: updates });
        })
        .then(function () {
          return Cart.request(routes.cartAdd, { items: items });
        })
        .then(function (data) {
          /* Only drop the snapshot once the old cart is actually back — if the
             restore fails the shopper can try the undo again. */
          Cart.clearSnapshot();
          Cart.render(data);
          return data;
        });
    },

    updateNote: function (note) {
      return this.request(routes.cartUpdate, { note: note });
    }
  };
  window.themeCart = Cart;

  /* ------------------------------------------------------------------ *
   * Add-to-cart buttons (grid cards, product form, sticky bar)
   * ------------------------------------------------------------------ */
  function handleBuyClick(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    // Products flagged with the direct checkout metafield skip the drawer.
    if (btn.getAttribute('data-direct-checkout') === 'true') {
      window.location.href = '/cart/' + variantId + ':1';
      return;
    }

    var qtyInput = btn.form ? btn.form.querySelector('[name="quantity"]') : null;
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = T.strings.adding;

    Cart.add(variantId, qty)
      .catch(function (err) {
        var live = $('#CartLiveRegion');
        if (live) live.textContent = err.message;
      })
      .then(function () {
        btn.disabled = false;
        btn.textContent = label;
      });
  }

  document.addEventListener('click', function (e) {
    var buy = e.target.closest('[data-add-to-cart]');
    if (buy) {
      e.preventDefault();
      e.stopPropagation();
      handleBuyClick(buy);
      return;
    }

    var openCart = e.target.closest('[data-cart-open]');
    if (openCart) { e.preventDefault(); Cart.open(); return; }

    var closeCart = e.target.closest('[data-cart-close]');
    if (closeCart) { e.preventDefault(); Cart.close(); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (Cart.isOpen()) Cart.close();
    var popup = $('.welcome-popup.is-open');
    if (popup) closeWelcomePopup(popup);
    $$('[data-nav-dropdown]').forEach(function (dd) { setDropdown(dd, false); });
  });

  /* ------------------------------------------------------------------ *
   * Cart drawer internals — rebound after every re-render
   * ------------------------------------------------------------------ */
  var reservationTimer = null;

  function bindCartDrawer() {
    var drawer = $('[data-cart-drawer]');
    if (!drawer) return;

    $$('[data-qty-change]', drawer).forEach(function (btn) {
      on(btn, 'click', function () {
        if (Cart.isBusy(drawer)) return;
        var key = btn.getAttribute('data-line-key');
        var qty = parseInt(btn.getAttribute('data-qty-change'), 10);
        Cart.setBusy(drawer, true);
        Cart.change(key, qty)
          .then(function () { Cart.setBusy(drawer, false); })
          .catch(function () { Cart.setBusy(drawer, false); });
      });
    });

    var upsell = $('[data-cart-upsell-add]', drawer);
    on(upsell, 'click', function () {
      if (Cart.isBusy(drawer)) return;
      Cart.setBusy(drawer, true);
      Cart.swapTo(upsell.getAttribute('data-variant-id'))
        .then(function () { Cart.setBusy(drawer, false); })
        .catch(function () { Cart.setBusy(drawer, false); });
    });

    /* The undo prompt belongs on screen only while the upgrade is actually in
       effect: a snapshot exists AND the upsell product is still in the cart.
       Without the second test the snapshot outlives the upgrade — remove the
       bundle by hand, or add something else, and the prompt would linger for
       the rest of the session offering to undo an upgrade that is already
       gone. */
    if (drawer.getAttribute('data-upsell-in-cart') !== 'true') Cart.clearSnapshot();

    var undoWrap = $('[data-cart-undo-wrap]', drawer);
    if (undoWrap) undoWrap.hidden = !Cart.hasSnapshot();

    var undo = $('[data-cart-undo]', drawer);
    on(undo, 'click', function () {
      if (Cart.isBusy(drawer)) return;
      Cart.setBusy(drawer, true);
      Cart.undoSwap()
        .then(function () { Cart.setBusy(drawer, false); })
        .catch(function () { Cart.setBusy(drawer, false); });
    });

    var note = $('[data-cart-note]', drawer);
    if (note) {
      var noteTimer;
      on(note, 'input', function () {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(function () { Cart.updateNote(note.value); }, 500);
      });
    }

    startReservationTimer(drawer);
  }

  function startReservationTimer(drawer) {
    var el = $('[data-reservation-timer]', drawer);
    clearInterval(reservationTimer);
    if (!el) return;
    var total = parseInt(el.getAttribute('data-minutes'), 10) * 60;
    if (!total) return;
    var left = total;
    var tick = function () {
      el.textContent = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
      left = left <= 0 ? total : left - 1;
    };
    tick();
    reservationTimer = setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------ *
   * Header — products dropdown + mobile menu
   * ------------------------------------------------------------------ */
  function setDropdown(dropdown, open) {
    var toggle = $('[data-nav-toggle]', dropdown);
    var panel = $('[data-nav-panel]', dropdown);
    if (!toggle || !panel) return;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.classList.toggle('is-open', open);
  }

  function initHeader() {
    $$('[data-nav-dropdown]').forEach(function (dropdown) {
      var toggle = $('[data-nav-toggle]', dropdown);
      on(toggle, 'click', function (e) {
        e.preventDefault();
        var panel = $('[data-nav-panel]', dropdown);
        setDropdown(dropdown, panel.hidden);
      });
    });

    document.addEventListener('pointerdown', function (e) {
      $$('[data-nav-dropdown]').forEach(function (dropdown) {
        if (!dropdown.contains(e.target)) setDropdown(dropdown, false);
      });
    });

    $$('[data-menu-toggle]').forEach(function (btn) {
      on(btn, 'click', function () {
        var menu = $('[data-mobile-menu]');
        if (!menu) return;
        var open = menu.hidden;
        menu.hidden = !open;
        btn.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * FAQ accordions
   * ------------------------------------------------------------------ */
  function initAccordions() {
    $$('[data-accordion]').forEach(function (list) {
      var single = list.getAttribute('data-accordion') === 'single';
      $$('.faq-question', list).forEach(function (btn) {
        on(btn, 'click', function () {
          var expanded = btn.getAttribute('aria-expanded') === 'true';
          if (single) {
            $$('.faq-question', list).forEach(function (other) {
              other.setAttribute('aria-expanded', 'false');
            });
          }
          btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Rolling countdown (ticker + product offer timer share one deadline)
   * ------------------------------------------------------------------ */
  var COUNTDOWN_KEY = 'theme-rolling-countdown-end';

  function countdownRemaining(minutes) {
    var now = Date.now();
    var end = 0;
    try { end = parseInt(localStorage.getItem(COUNTDOWN_KEY), 10) || 0; } catch (e) { end = 0; }
    if (!end || end < now) {
      end = now + minutes * 60 * 1000;
      try { localStorage.setItem(COUNTDOWN_KEY, String(end)); } catch (e) { /* private mode */ }
    }
    return Math.max(0, Math.floor((end - now) / 1000));
  }

  function initCountdowns() {
    var targets = $$('[data-countdown]');
    if (!targets.length) return;
    var minutes = T.countdownMinutes || 15;

    var tick = function () {
      var left = countdownRemaining(minutes);
      var hrs = Math.floor(left / 3600);
      var mins = Math.floor((left % 3600) / 60);
      var secs = left % 60;
      targets.forEach(function (root) {
        var withHours = root.getAttribute('data-countdown') === 'hours';
        var h = $('[data-countdown-hours]', root);
        var m = $('[data-countdown-minutes]', root);
        var s = $('[data-countdown-seconds]', root);
        if (h) h.textContent = pad(hrs);
        if (m) m.textContent = pad(withHours ? mins : Math.floor(left / 60));
        if (s) s.textContent = pad(secs);
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------ *
   * Live viewer counter — range comes from section settings
   * ------------------------------------------------------------------ */
  function initViewerCounters() {
    var els = $$('[data-viewers]');
    if (!els.length) return;
    var schedule = function () {
      els.forEach(function (el) {
        var min = parseInt(el.getAttribute('data-viewers-min'), 10) || 40;
        var max = parseInt(el.getAttribute('data-viewers-max'), 10) || 60;
        if (max < min) max = min;
        el.textContent = min + Math.floor(Math.random() * (max - min + 1));
      });
      setTimeout(schedule, 3000 + Math.random() * 2000);
    };
    schedule();
  }

  /* ------------------------------------------------------------------ *
   * Welcome popup
   * ------------------------------------------------------------------ */
  function closeWelcomePopup(popup) {
    popup.classList.remove('is-open');
    unlockScroll();
    document.dispatchEvent(new CustomEvent('theme:overlay:close'));
    try { sessionStorage.setItem(popup.getAttribute('data-storage-key'), '1'); } catch (e) { /* noop */ }
  }

  function initWelcomePopup() {
    var popup = $('[data-welcome-popup]');
    if (!popup) return;
    var key = popup.getAttribute('data-storage-key');
    var seen = false;
    try { seen = !!sessionStorage.getItem(key); } catch (e) { seen = false; }
    if (seen && !window.Shopify?.designMode) return;

    var delay = (parseFloat(popup.getAttribute('data-delay')) || 0) * 1000;
    setTimeout(function () {
      popup.classList.add('is-open');
      lockScroll();
      document.dispatchEvent(new CustomEvent('theme:overlay:open'));
    }, delay);

    $$('[data-popup-close]', popup).forEach(function (btn) {
      on(btn, 'click', function () { closeWelcomePopup(popup); });
    });

    var code = $('[data-copy-code]', popup);
    on(code, 'click', function () {
      var value = code.getAttribute('data-copy-code');
      var label = $('[data-copy-label]', popup);
      if (navigator.clipboard) navigator.clipboard.writeText(value);
      if (label) {
        var original = label.textContent;
        label.textContent = T.strings.copied;
        setTimeout(function () { label.textContent = original; }, 1600);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Live sales pop-ups — one card per "Sale notification" block
   * ------------------------------------------------------------------ */
  function initSalesPops() {
    var root = $('[data-sales-pops]');
    if (!root) return;
    var card = $('[data-sales-pop-card]', root);
    var entries = $$('[data-sales-entry]', root).map(function (el) {
      return {
        name: el.getAttribute('data-name'),
        product: el.getAttribute('data-product'),
        url: el.getAttribute('data-url'),
        image: el.getAttribute('data-image'),
        time: el.getAttribute('data-time')
      };
    });
    if (!card || !entries.length) return;

    var storageKey = root.getAttribute('data-storage-key');
    var dismissed = false;
    try { dismissed = !!sessionStorage.getItem(storageKey); } catch (e) { dismissed = false; }
    if (dismissed) return;

    var initialDelay = (parseFloat(root.getAttribute('data-initial-delay')) || 8) * 1000;
    var duration = (parseFloat(root.getAttribute('data-duration')) || 5) * 1000;
    var gapMin = (parseFloat(root.getAttribute('data-gap-min')) || 15) * 1000;
    var gapMax = (parseFloat(root.getAttribute('data-gap-max')) || 25) * 1000;
    if (gapMax < gapMin) gapMax = gapMin;

    var overlayOpen = false;
    var stopped = false;
    var lastIndex = -1;
    var timer = null;
    var current = null;

    document.addEventListener('theme:overlay:open', function () { overlayOpen = true; });
    document.addEventListener('theme:overlay:close', function () { overlayOpen = false; });

    function show() {
      if (stopped) return;
      if (overlayOpen) { timer = setTimeout(show, 3000); return; }

      var index;
      do { index = Math.floor(Math.random() * entries.length); }
      while (entries.length > 1 && index === lastIndex);
      lastIndex = index;
      current = entries[index];

      $('[data-sales-pop-name]', card).textContent = current.name;
      $('[data-sales-pop-product]', card).textContent = current.product;
      $('[data-sales-pop-time]', card).textContent = current.time;
      var img = $('[data-sales-pop-image]', card);
      if (img) {
        if (current.image) { img.src = current.image; img.hidden = false; }
        else { img.hidden = true; }
      }

      card.hidden = false;
      // Force a frame so the slide-in transition runs from its start position.
      requestAnimationFrame(function () { card.classList.add('is-visible'); });

      timer = setTimeout(function () {
        card.classList.remove('is-visible');
        timer = setTimeout(show, gapMin + Math.random() * (gapMax - gapMin));
      }, duration);
    }

    on(card, 'click', function (e) {
      if (e.target.closest('[data-sales-pop-dismiss]')) return;
      if (current && current.url) window.location.href = current.url;
    });

    on($('[data-sales-pop-dismiss]', card), 'click', function (e) {
      e.stopPropagation();
      stopped = true;
      clearTimeout(timer);
      card.classList.remove('is-visible');
      try { sessionStorage.setItem(storageKey, '1'); } catch (err) { /* noop */ }
    });

    timer = setTimeout(show, initialDelay);
  }

  /* ------------------------------------------------------------------ *
   * Product page — gallery, review carousel, sticky bar
   * ------------------------------------------------------------------ */
  function initProductGallery() {
    var gallery = $('[data-product-gallery]');
    if (!gallery) return;
    var main = $('[data-gallery-main]', gallery);
    $$('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      on(thumb, 'click', function () {
        main.src = thumb.getAttribute('data-full');
        main.srcset = '';
        $$('[data-gallery-thumb]', gallery).forEach(function (t) { t.setAttribute('aria-current', 'false'); });
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  function initReviewCarousel() {
    $$('[data-review-carousel]').forEach(function (root) {
      var slides = $$('[data-review-slide]', root);
      var dots = $$('[data-review-dot]', root);
      if (slides.length < 1) return;
      var index = 0;

      function paint() {
        slides.forEach(function (s, i) { s.hidden = i !== index; });
        dots.forEach(function (d, i) { d.setAttribute('data-active', i === index ? 'true' : 'false'); });
      }
      function step(delta) {
        index = (index + delta + slides.length) % slides.length;
        paint();
      }
      /* The product page ships a single "next" chevron; prev and dots stay
         supported for any other section that renders them. */
      on($('[data-review-prev]', root), 'click', function () { step(-1); });
      on($('[data-review-next]', root), 'click', function () { step(1); });
      paint();
    });
  }

  /* ------------------------------------------------------------------ *
   * Viewport height — embedded webviews
   * ------------------------------------------------------------------ */
  /* Telegram, Instagram and other in-app browsers report 100dvh against a
     viewport that includes the chrome physically covering the screen, so a
     full-height panel came up short with a gap top and bottom. visualViewport
     is the part actually visible, which is what the cart drawer needs. */
  function trackViewportHeight() {
    function apply() {
      var vv = window.visualViewport;
      var h = Math.round((vv && vv.height) || window.innerHeight || 0);
      if (h > 0) document.documentElement.style.setProperty('--viewport-height', h + 'px');
    }
    apply();
    on(window, 'resize', apply);
    on(window, 'orientationchange', function () { setTimeout(apply, 150); });
    if (window.visualViewport) {
      on(window.visualViewport, 'resize', apply);
      on(window.visualViewport, 'scroll', apply);
    }
  }

  /* ------------------------------------------------------------------ *
   * Content protection
   * ------------------------------------------------------------------ */
  /* Deterrents, not security. Everything the browser renders is still reachable
     through its own menus, view-source or with JavaScript off — these only make
     casual copying inconvenient. Form fields are exempt throughout so the
     contact form, quantity box and order note stay usable. */
  function initContentProtection() {
    var p = T.protect || {};
    /* Never in the theme editor — a merchant has to be able to select and
       right-click their own content while building the store. */
    if (window.Shopify && window.Shopify.designMode) return;

    function inFormField(el) {
      return !!(el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]'));
    }

    if (p.text) document.documentElement.classList.add('no-select');
    if (p.images) document.documentElement.classList.add('no-image-save');

    if (p.rightClick) {
      on(document, 'contextmenu', function (e) {
        if (inFormField(e.target)) return;
        e.preventDefault();
      });
    }

    if (p.images) {
      on(document, 'dragstart', function (e) {
        if (e.target && e.target.tagName === 'IMG') e.preventDefault();
      });
    }

    if (p.devtools) {
      on(document, 'keydown', function (e) {
        var key = (e.key || '').toLowerCase();
        var mod = e.ctrlKey || e.metaKey;
        /* Copy and paste are deliberately left alone: with selection blocked
           there is nothing to copy anyway, and breaking them inside a form
           field would only punish real customers. */
        if (key === 'f12') { e.preventDefault(); return; }
        if (mod && e.shiftKey && (key === 'i' || key === 'j' || key === 'c')) { e.preventDefault(); return; }
        if (mod && (key === 'u' || key === 's') && !inFormField(e.target)) e.preventDefault();
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Hero decorations — keep the stars and sparkles off the text
   * ------------------------------------------------------------------ */
  /* The glyphs are placed by eye in the approved design, at percentages of the
     hero. That holds at the width it was drawn for, but the text rewraps at
     other widths and a glyph can end up sitting on a word. Reserving a column
     in CSS would fix it, but only by narrowing the copy until it rewrapped —
     trading one regression for another.

     So measure instead: take each glyph's vertical band, find the real line
     boxes of any text crossing that band, and push the glyph outward by exactly
     the overlap. Glyphs that already clear the text — every one of them on
     desktop — are left untouched. */
  /* scale(1.1) rotated 8deg sweeps a box about 1.1 * (cos8 + sin8) = 1.24x the
     glyph's own size; round up for the stroke. */
  var TWINKLE_PEAK = 1.26;

  function initHeroDecorations() {
    var layer = $('[data-hero-decorations]');
    if (!layer) return;
    var hero = layer.parentElement;
    var glyphs = $$('.hero-deco', layer);
    if (!glyphs.length) return;

    var gap = parseFloat(layer.getAttribute('data-clearance'));
    if (isNaN(gap)) gap = 8;
    var enabled = layer.getAttribute('data-keep-clear') !== 'false';

    /* Walk the text nodes rather than calling selectNodeContents on each block:
       a range over a block's contents also yields the block's own full-width
       box, and no glyph can ever clear that — it would peg every push to the
       maximum and still read as an overlap. Text nodes give the real ink. */
    function textLines() {
      var rects = [];
      $$('.hero-badge, .hero-title, .hero-caption, .hero-paragraph, .hero-inner .btn', hero)
        .forEach(function (el) {
          var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
          var node;
          while ((node = walker.nextNode())) {
            if (!node.nodeValue || !node.nodeValue.trim()) continue;
            var range = document.createRange();
            range.selectNodeContents(node);
            Array.prototype.forEach.call(range.getClientRects(), function (r) {
              if (r.width > 1 && r.height > 1) rects.push(r);
            });
          }
        });
      return rects;
    }

    function place() {
      glyphs.forEach(function (g) {
        g.style.removeProperty('--deco-push');
        g.removeAttribute('data-deco-hidden');
      });
      if (!enabled) return;

      var lines = textLines();
      if (!lines.length) return;

      glyphs.forEach(function (g) {
        var isRight = g.classList.contains('hero-deco-r');
        var isLeft = g.classList.contains('hero-deco-l');
        if (!isRight && !isLeft) return;   // free glyphs sit in clear air by design

        /* Measure the box the glyph sweeps over the whole twinkle, not the box
           it happens to occupy right now. The animation scales to 1.1 and
           rotates 8 degrees, so mid-animation it is up to a quarter larger than
           at rest — clear it at rest and it still clips a word a second later. */
        var box = g.getBoundingClientRect();
        /* SVG elements are not HTMLElements, so offsetWidth is undefined on
           them — read the untransformed size from the computed style. */
        var cs = getComputedStyle(g);
        var baseW = parseFloat(cs.width) || box.width;
        var baseH = parseFloat(cs.height) || box.height;
        var halfW = baseW * TWINKLE_PEAK / 2;
        var halfH = baseH * TWINKLE_PEAK / 2;
        var cx = box.left + box.width / 2;
        var cy = box.top + box.height / 2;
        var r = { left: cx - halfW, right: cx + halfW, top: cy - halfH, bottom: cy + halfH };
        var push = 0;
        lines.forEach(function (t) {
          if (r.bottom <= t.top || r.top >= t.bottom) return;   // no vertical band overlap
          if (isRight && r.left < t.right + gap) push = Math.max(push, t.right + gap - r.left);
          if (isLeft && r.right > t.left - gap) push = Math.max(push, r.right - (t.left - gap));
        });
        if (!push) return;

        /* A push can only travel as far as the glyph's own offset from the
           edge — the stylesheet floors left/right at 0. Asking for more than
           that would be silently truncated and leave the glyph on the text, so
           when the room is not there the glyph is hidden instead. Text is never
           obscured; that is the whole point of the setting. */
        var maxPush = parseFloat(isRight ? cs.right : cs.left);
        if (isNaN(maxPush)) maxPush = 0;
        if (push > maxPush) g.setAttribute('data-deco-hidden', '');
        else g.style.setProperty('--deco-push', Math.ceil(push) + 'px');
      });
    }

    place();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(place);

    var pending;
    function schedule() { clearTimeout(pending); pending = setTimeout(place, 120); }
    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(hero);
  }

  function initStickyBar() {
    var bar = $('[data-sticky-bar]');
    if (!bar) return;
    /* Watch the main buy button, so the bar appears exactly when the real CTA
       leaves the viewport. Falls back to the end-of-column anchor. */
    var target = $('[data-sticky-trigger]') || $('[data-sticky-anchor]');
    if (!target) return;
    /* The sales pop-up needs to know when to sit above the bar. Publishing the
       state on the root element — with the bar's measured height — keeps that
       to CSS, and means the pop-up is unaffected on every page that has no
       sticky bar at all. */
    function publish(visible) {
      document.documentElement.classList.toggle('sticky-bar-visible', visible);
      if (visible) {
        document.documentElement.style.setProperty(
          '--sticky-bar-height', Math.round(bar.getBoundingClientRect().height) + 'px');
      }
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var visible = !entry.isIntersecting;
        bar.classList.toggle('is-visible', visible);
        publish(visible);
      });
    }, { threshold: 0 });
    observer.observe(target);
  }

  function initVariantPicker() {
    $$('[data-variant-picker]').forEach(function (select) {
      var form = select.closest('form');
      on(select, 'change', function () {
        var id = select.value;
        var input = form ? form.querySelector('[name="id"]') : null;
        if (input) input.value = id;
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
        var option = select.options[select.selectedIndex];
        /* The price appears twice on a product page — in the offer column and
           in the sticky bar — so update every instance, not just the first. */
        if (option.dataset.price) {
          $$('[data-variant-price]').forEach(function (el) { el.textContent = option.dataset.price; });
        }
        $$('[data-variant-compare]').forEach(function (el) {
          el.textContent = option.dataset.compare || '';
          el.hidden = !option.dataset.compare;
        });
        $$('[data-add-to-cart]').forEach(function (btn) {
          btn.setAttribute('data-variant-id', id);
          btn.disabled = option.dataset.available !== 'true';
        });
      });
    });
  }

  function initQuantity() {
    $$('[data-quantity]').forEach(function (wrap) {
      var input = $('input', wrap);
      $$('[data-quantity-change]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var delta = parseInt(btn.getAttribute('data-quantity-change'), 10);
          var next = (parseInt(input.value, 10) || 1) + delta;
          input.value = Math.max(1, next);
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function init() {
    initHeader();
    initAccordions();
    initCountdowns();
    initViewerCounters();
    initWelcomePopup();
    initSalesPops();
    initProductGallery();
    initReviewCarousel();
    initStickyBar();
    initHeroDecorations();
    trackViewportHeight();
    initContentProtection();
    initVariantPicker();
    initQuantity();
    bindCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-bind after a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target.querySelector('[data-cart-drawer]')) Cart.open();
    var popup = e.target.querySelector('[data-welcome-popup]');
    if (popup) popup.classList.add('is-open');
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target.querySelector('[data-cart-drawer]')) Cart.close();
    var popup = e.target.querySelector('[data-welcome-popup]');
    if (popup) popup.classList.remove('is-open');
  });
})();
